import CustomPageService from 'services/course-handler-services/custom-page-service';
import LocaleService from 'services/loader-handler-services/locale-service';
import PageService from 'services/page-service';
import EF from 'ef/ef';
import _ from 'lodash';
import i18next from 'i18next';
import utils from 'lib/utils';

export default class InitializationService {
  static applyTransformations(modulesJSON, modulesContentJson, groupsJSON) {

    // this deep merges a customization to replace locale strings
    // The customization object is shaped like this
    // {
    //   "type": "locale-content-customization",
    //   "en": {
    //     "library": {
    //       "atoms": {
    //         "accordion-atom": {
    //           "pageDescription": "BINGO BANGO"
    //         }
    //       }
    //     }
    //   }
    // }
    const localeCustomization = EF.customizations.findByType('locale-content-customization');
    if (localeCustomization && localeCustomization.has(i18next.language)) {
      const mergable = localeCustomization.get(i18next.language);
      if (mergable) {
        _.merge(LocaleService.langContent, mergable);
        if (i18next.store && i18next.store.data && i18next.store.data[i18next.language]) {
          _.merge(i18next.store.data[i18next.language], mergable);
        }
      }
    }

    // Build a promise for the custom-locales fetch instead of firing it and forgetting it.
    let customLocalesPromise = Promise.resolve();

    const customLocaleCustomization = EF.customizations.findByType('page-content-customization');
    if (customLocaleCustomization && customLocaleCustomization.has('custom_locales_url')) {
      const customLocalesUrl = customLocaleCustomization.get('custom_locales_url');
      if (customLocalesUrl) {
        customLocalesPromise = fetch(customLocalesUrl)
          .then(r => r.json())
          .then(map => {
            if (map !== null && typeof map === 'object') {
              const currentLocale = LocaleService.getLanguage();
              Object.entries(map).forEach(([locale, mergable]) => {
                if (locale === currentLocale && mergable) {
                  if (currentLocale.split('-').length > 1) {
                    _.merge(LocaleService.localeContent, mergable);
                  } else {
                    _.merge(LocaleService.langContent, mergable);
                  }
                }
              });
            }
          })
          .catch(e => {
            console.warn('[InitializationService] custom locales fetch failed:', e); //eslint-disable-line no-console
          });
      }
    }

    // Wait for the locale merge to finish before doing anything that reads it.
    return customLocalesPromise.then(() =>
      CustomPageService.replaceCustomPages(modulesJSON, modulesContentJson, groupsJSON).then(
        ([modulesJSON, modulesContentJson, groupsJSON]) => {
          // this removes any activities where
          //                             a) all of the pages are setup to skip if a customization doesn't exist
          //                             b) the customizations do not exist, so the entire activity would be skipped
          // this also removes modules where all activities meet the above condition as well
          modulesJSON.forEach(module => {
            // We'll need to store the page that is skipped at the end of the last activity in a module
            if (module.activities) {
              const lastActivity = utils.getLastArrayElement(module.activities);
              module.activities.forEach(activity => {
                const pages = activity.pages;
                if (pages) {
                  try {
                    pages.slice().forEach((page, index) => {
                      if (modulesContentJson[module.key][activity.key][page.key]) {
                        const skip = modulesContentJson[module.key][activity.key][page.key].skip;
                        if (skip) {
                          const { customization: customizationType, slug } = skip;
                          let customization;
                          if (customizationType) {
                            customization = EF.customizations.getByKeyAndSlug(customizationType, slug);
                          }
                          if (!customization) {
                            customization = EF.customizations.find(c => {
                              if (
                                (c.get('type') === 'page-content-customization' ||
                                  c.get('type') === 'customization-page_content_customizations') &&
                                c.has('customizations_map')
                              ) {
                                const map = c.get('customizations_map');
                                // return true if the inner most object of `map` has a key that matches the slug
                                return (function traverse(obj) {
                                  return Object.entries(obj).reduce((result, [key, value]) => {
                                    if (result) {
                                      return result;
                                    }
                                    if (value !== null && typeof value === 'object') {
                                      return traverse(value);
                                    }
                                    return key === slug;
                                  }, false);
                                })(map);
                              }
                            });
                          }
                          // Remove from course map pages marked for skip in full scorm
                          let skip_fullSCORM = false;
                          if (EF.config.get('fullSCORM')) {
                            skip_fullSCORM = skip.fullSCORM;
                          }

                          // Remove from course map if learner profile setting don't match the student
                          let skip_learner = false;
                          if (skip.learner_profile) {
                            skip_learner = PageService.shouldSkipLearner(skip);
                          }
                          // This logic is a bit tricky, here are the conditions enumerated
                          // if skip.when === present  - remove page if the customization is present
                          //                           - do not remove page if customization is absent
                          // if skip.when !== present  - remove page if the customization is absent
                          //                           - do not remove page if the customization is present
                          let skip_customization = false;
                          if (customizationType && slug) {
                            skip_customization =
                              (skip.when !== 'present' && (customization === undefined || customization === null)) ||
                              (skip.when === 'present' && customization !== undefined && customization !== null);
                          }

                          let skipResource = false;
                          if (skip.resource_type) {
                            skipResource =
                              EF.resources.byTypeSync(skip.resource_type, skip.resource_sub_type).length === 0;
                          }
                          if (skipResource || skip_customization || skip_learner || skip_fullSCORM) {
                            /**
                             * when the final page of the module is skipped, it causes module completion issues on the platform.
                             * We’re generally going to prevent using skipped pages at the end of modules,
                             * but this would also cause problems with course mod if someone skipped the last page of a module.
                             *
                             * Thus we need to check if the skipped page is the last page of the module. If it is,
                             * save the page progress when the module completes.
                             */

                            // Order pages by index. This is the index defined in the module.json, not an array index
                            // Note: before ordering moduleJSON pages by index, the index used to delete skipped pages was an array index;
                            activity.pages = CustomPageService.orderPagesByIndex(activity.pages);

                            // then check if the last page is the page being skipped.
                            const lastPage = utils.getLastArrayElement(activity.pages);

                            if (page.index === lastPage.index && lastActivity.key === activity.key) {
                              // get the necessary data for the skipped page
                              const skippedPageData = {
                                module_key: module.key,
                                activity_key: activity.key,
                                key: page.key,
                                page_key: page.key,
                                index: page.index,
                              };

                              // store the skipped page so that we use them later in module progress
                              module.skippedLastPage = skippedPageData;
                            }

                            const deletionIndex = activity.pages.findIndex(p => p.key === page.key);
                            if (deletionIndex !== -1) {
                              // remove the page from both collections
                              activity.pages.splice(deletionIndex, 1);
                              delete modulesContentJson[module.key][activity.key][page.key];
                            }
                          }
                        }
                      }
                      return false;
                    });
                  } catch (e) {
                    console.warn('Initaliation service warning:', e); //eslint-disable-line no-console
                  }
                  if (activity.pages.length <= 0) {
                    module.activities = module.activities.filter(a => a.key !== activity.key);
                    delete modulesContentJson[module.key][activity.key];
                  }
                }
              });
              if (module.activities.length <= 0) {
                modulesJSON = modulesJSON.filter(m => m.key !== module.key);
                delete modulesContentJson[module.key];
              }
            }
          });
          return Promise.resolve([modulesJSON, modulesContentJson, groupsJSON]);
        }
      )
    );
  }
}
